import React, { useState, useEffect } from 'react';
import { 
  Cloud, 
  Shield, 
  Zap, 
  RefreshCw, 
  CheckCircle2, 
  ArrowRight, 
  Server, 
  Terminal, 
  Settings, 
  AlertCircle, 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  ChevronRight,
  Users,
  Compass
} from 'lucide-react';

export default function App() {
  // Navigation active states and scroll tracking
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    service: 'Cloud Assessment'
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  // Scroll effect to make navigation compact and glassmorphic
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      // Track active section for navigation highlighting
      const sections = ['inicio', 'problema', 'solucion', 'servicios', 'planes', 'proceso', 'contacto'];
      const scrollPosition = window.scrollY + 120;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const top = element.offsetTop;
          const height = element.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Form submit handler with actual API Gateway integration
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simple frontend validation
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus('error');
      setFormMessage('Por favor, completa los campos requeridos (Nombre, Email y Mensaje).');
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    // Fetch API endpoint from Vite Environment variables (populated during deployment)
    const apiEndpoint = import.meta.env.VITE_API_URL;

    try {
      if (apiEndpoint) {
        // Real AWS API Gateway call
        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          setFormStatus('success');
          setFormMessage('¡Muchas gracias! Tu solicitud ha sido registrada correctamente. Un asesor técnico de CloudGatePY se pondrá en contacto contigo a la brevedad.');
          setFormData({
            name: '',
            company: '',
            email: '',
            phone: '',
            message: '',
            service: 'Cloud Assessment'
          });
        } else {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || 'Error en la respuesta del servidor');
        }
      } else {
        // Fallback for development/demo without fully deployed backend
        await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulate latency
        console.log('Local/Demo Form Submission:', formData);
        setFormStatus('success');
        setFormMessage('¡Solicitud Enviada (Modo Demostración)! Tu mensaje ha sido registrado exitosamente en nuestro simulador. En producción, esto se guarda en AWS DynamoDB de manera directa.');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          message: '',
          service: 'Cloud Assessment'
        });
      }
    } catch (error: any) {
      console.error('Error submitting form:', error);
      setFormStatus('error');
      setFormMessage(error.message || 'Ocurrió un error al enviar tu solicitud. Por favor, intenta de nuevo o comunícate vía WhatsApp.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Utility to scroll directly and close mobile menu
  const scrollToId = (id: string) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div>
      {/* Dynamic Glow Particles Background */}
      <div className="particle-background">
        <div className="cloud-glow-1"></div>
        <div className="cloud-glow-2"></div>
      </div>

      {/* Header & Navigation Bar */}
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="container header-container">
          <a href="#inicio" className="logo" onClick={(e) => { e.preventDefault(); scrollToId('inicio'); }}>
            <Cloud className="logo-icon" size={28} />
            <span>Cloud<span className="text-teal-gradient">GatePY</span></span>
          </a>

          {/* Desktop Navigation */}
          <nav>
            <ul className="nav-links">
              <li>
                <a 
                  href="#inicio" 
                  className={`nav-link ${activeSection === 'inicio' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); scrollToId('inicio'); }}
                >
                  Inicio
                </a>
              </li>
              <li>
                <a 
                  href="#problema" 
                  className={`nav-link ${activeSection === 'problema' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); scrollToId('problema'); }}
                >
                  Problemas Comunes
                </a>
              </li>
              <li>
                <a 
                  href="#servicios" 
                  className={`nav-link ${activeSection === 'servicios' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); scrollToId('servicios'); }}
                >
                  Servicios
                </a>
              </li>
              <li>
                <a 
                  href="#planes" 
                  className={`nav-link ${activeSection === 'planes' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); scrollToId('planes'); }}
                >
                  Planes
                </a>
              </li>
              <li>
                <a 
                  href="#proceso" 
                  className={`nav-link ${activeSection === 'proceso' ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); scrollToId('proceso'); }}
                >
                  Proceso
                </a>
              </li>
              <li>
                <button 
                  className="btn btn-secondary" 
                  style={{ padding: '0.5rem 1.2rem', fontSize: '0.9rem' }}
                  onClick={() => scrollToId('contacto')}
                >
                  Presupuesto
                </button>
              </li>
            </ul>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="menu-btn" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation Dropdown */}
        <ul className={`nav-links mobile-nav ${mobileMenuOpen ? 'active' : ''}`}>
          <li>
            <a 
              href="#inicio" 
              className="nav-link" 
              onClick={(e) => { e.preventDefault(); scrollToId('inicio'); }}
            >
              Inicio
            </a>
          </li>
          <li>
            <a 
              href="#problema" 
              className="nav-link" 
              onClick={(e) => { e.preventDefault(); scrollToId('problema'); }}
            >
              Problemas Comunes
            </a>
          </li>
          <li>
            <a 
              href="#servicios" 
              className="nav-link" 
              onClick={(e) => { e.preventDefault(); scrollToId('servicios'); }}
            >
              Servicios
            </a>
          </li>
          <li>
            <a 
              href="#planes" 
              className="nav-link" 
              onClick={(e) => { e.preventDefault(); scrollToId('planes'); }}
            >
              Planes
            </a>
          </li>
          <li>
            <a 
              href="#proceso" 
              className="nav-link" 
              onClick={(e) => { e.preventDefault(); scrollToId('proceso'); }}
            >
              Proceso
            </a>
          </li>
          <li>
            <button 
              className="btn btn-primary" 
              style={{ width: '100%' }}
              onClick={() => scrollToId('contacto')}
            >
              Solicitar Presupuesto
            </button>
          </li>
        </ul>
      </header>

      {/* 1. HERO SECTION */}
      <section id="inicio" className="section" style={{ paddingTop: '8rem', paddingBottom: '6rem' }}>
        <div className="container hero-wrapper">
          <div className="hero-content">
            <div className="hero-tag">
              <Zap size={14} />
              <span>La puerta de tu negocio a la nube</span>
            </div>
            <h1 className="hero-title text-gradient">
              Llevamos tu empresa a la nube, de forma segura y automatizada
            </h1>
            <p className="hero-subtitle">
              CloudGatePY ayuda a empresas y profesionales en Paraguay a evolucionar llevando cualquier sistema o aplicación actual a la nube. Además, diseñamos y desarrollamos soluciones tecnológicas a medida adaptadas a las necesidades de tu negocio, impulsando la productividad, continuidad y seguridad que necesitás para crecer.
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary" onClick={() => scrollToId('contacto')}>
                Solicitar presupuesto
                <ArrowRight size={16} />
              </button>
              <button className="btn btn-secondary" onClick={() => scrollToId('servicios')}>
                Ver servicios
              </button>
            </div>
          </div>
          
          <div className="hero-visual">
            <div className="hero-visual-bg"></div>
            {/* Tech Visual Component: High quality customized SVG */}
            <svg viewBox="0 0 500 500" fill="none" className="hero-visual-svg" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="cyan-teal" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#00D8FF" />
                  <stop offset="100%" stopColor="#00ADB5" />
                </linearGradient>
                <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="15" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
              </defs>
              {/* Outer Orbit */}
              <circle cx="250" cy="250" r="180" stroke="url(#cyan-teal)" strokeWidth="1" strokeDasharray="5,10" opacity="0.3" />
              <circle cx="250" cy="250" r="140" stroke="url(#cyan-teal)" strokeWidth="1" opacity="0.15" />
              
              {/* Cloud Gateway Portal Central Shape */}
              <g filter="url(#glow)">
                <path d="M250,150 C290,150 310,180 310,210 C310,212 312,215 315,215 C335,215 345,230 345,245 C345,265 325,280 300,280 L200,280 C175,280 155,265 155,245 C155,230 165,215 185,215 C188,215 190,212 190,210 C190,180 210,150 250,150 Z" 
                  fill="none" stroke="url(#cyan-teal)" strokeWidth="3" opacity="0.9" />
              </g>

              {/* Floating tech elements connected */}
              <line x1="250" y1="150" x2="250" y2="80" stroke="#00ADB5" strokeWidth="1.5" strokeDasharray="4 4" />
              <line x1="300" y1="280" x2="380" y2="350" stroke="#00ADB5" strokeWidth="1.5" strokeDasharray="4 4" />
              <line x1="200" y1="280" x2="120" y2="350" stroke="#00ADB5" strokeWidth="1.5" strokeDasharray="4 4" />

              {/* Server Nodes */}
              <circle cx="250" cy="80" r="20" fill="#0B192C" stroke="#00D8FF" strokeWidth="2" />
              <path d="M242,80 L258,80 M242,75 L258,75 M242,85 L258,85" stroke="#00D8FF" strokeWidth="2" />

              {/* Secure database */}
              <circle cx="380" cy="350" r="25" fill="#0B192C" stroke="#00ADB5" strokeWidth="2" />
              <path d="M370,342 C370,338 390,338 390,342 M370,342 L370,358 C370,362 390,362 390,358 L390,342 M370,350 C370,354 390,354 390,350" stroke="#00ADB5" strokeWidth="2" fill="none" />

              {/* Secure Shield */}
              <circle cx="120" cy="350" r="25" fill="#0B192C" stroke="#00D8FF" strokeWidth="2" />
              <path d="M112,342 L120,337 L128,342 L128,350 C128,358 120,363 120,363 C120,363 112,358 112,350 Z" fill="none" stroke="#00D8FF" strokeWidth="2" />

              {/* Orbital Nodes */}
              <circle cx="390" cy="250" r="8" fill="#00D8FF" />
              <circle cx="110" cy="250" r="6" fill="#00ADB5" />
              <circle cx="250" cy="430" r="10" fill="#00D8FF" />
            </svg>
          </div>
        </div>
      </section>

      {/* 2. PROBLEM SECTION */}
      <section id="problema" className="section" style={{ background: 'linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)' }}>
        <div className="container">
          <div className="text-center">
            <span className="section-subtitle">Los riesgos del entorno actual</span>
            <h2 className="section-title">¿Los límites de tu tecnología actual frenan el crecimiento de tu empresa?</h2>
            <p className="section-desc">
              Depender de sistemas obsoletos, servidores físicos locales o herramientas limitadas expone a tu organización a constantes riesgos operativos, fallas de seguridad y frena la capacidad de competir al nivel que el mercado exige.
            </p>
          </div>

          <div className="problem-grid">
            {/* Card 1 */}
            <div className="glass-card problem-card">
              <div className="problem-icon-wrapper">
                <Server size={24} />
              </div>
              <h3 className="problem-title">Dependencia de Equipos Locales</h3>
              <p className="problem-desc">
                Tus sistemas de facturación, ventas o inventarios instalados en una sola PC de la oficina o en un servidor físico. Si ese equipo falla o se daña, tu negocio se paraliza por completo.
              </p>
            </div>

            {/* Card 2 */}
            <div className="glass-card problem-card">
              <div className="problem-icon-wrapper">
                <Shield size={24} style={{ color: '#ff4a4a' }} />
              </div>
              <h3 className="problem-title">Riesgo de Pérdida de Datos</h3>
              <p className="problem-desc">
                Falta de copias de seguridad automáticas y protección contra virus. Un corte de luz, robo de equipos o ataque digital puede hacerte perder de forma definitiva e irreversible toda tu facturación, ventas e información de clientes.
              </p>
            </div>

            {/* Card 3 */}
            <div className="glass-card problem-card">
              <div className="problem-icon-wrapper">
                <Users size={24} />
              </div>
              <h3 className="problem-title">Dependencia y Falta de Control</h3>
              <p className="problem-desc">
                Depender enteramente de soporte técnico externo ocasional para solucionar emergencias. Sin herramientas que te avisen a tiempo si algo va a fallar antes de que afecte la atención a tus clientes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. SOLUTION SECTION */}
      <section id="solucion" className="section">
        <div className="container solution-wrapper">
          <div className="solution-visual">
            <svg viewBox="0 0 500 400" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxWidth: '440px' }}>
              <defs>
                <linearGradient id="grad-cyan" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#00D8FF" />
                  <stop offset="100%" stopColor="#005B66" />
                </linearGradient>
              </defs>
              {/* Modern Graphic representing AWS Nube Evolution */}
              <rect x="50" y="80" width="400" height="240" rx="16" fill="#0B192C" stroke="#00D8FF" strokeWidth="1.5" opacity="0.6" />
              {/* Safe Lock Area */}
              <circle cx="250" cy="200" r="50" fill="url(#grad-cyan)" opacity="0.2" />
              <circle cx="250" cy="200" r="35" fill="#0B192C" stroke="#00D8FF" strokeWidth="2.5" />
              <path d="M244,195 L256,195 L256,208 C256,211 253,214 250,214 C247,214 244,211 244,208 Z" fill="#00D8FF" />
              <circle cx="250" cy="190" r="6" stroke="#00D8FF" strokeWidth="2.5" fill="none" />
              
              {/* Backups & Monitoring visual rings */}
              <path d="M150,150 C180,110 320,110 350,150" stroke="#00ADB5" strokeWidth="2" strokeDasharray="6 4" />
              <path d="M150,250 C180,290 320,290 350,250" stroke="#00ADB5" strokeWidth="2" strokeDasharray="6 4" />

              {/* Data Flow Nodes */}
              <circle cx="150" cy="150" r="8" fill="#00D8FF" />
              <circle cx="350" cy="150" r="8" fill="#00ADB5" />
              <circle cx="150" cy="250" r="8" fill="#00ADB5" />
              <circle cx="350" cy="250" r="8" fill="#00D8FF" />
            </svg>
          </div>

          <div className="solution-content">
            <span className="solution-badge">La Solución CloudGatePY</span>
            <h2 className="section-title" style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
              Tu aliado de confianza para la evolución tecnológica de tu empresa
            </h2>
            <p className="hero-subtitle" style={{ fontSize: '1.1rem', marginBottom: '2rem' }}>
              No nos limitamos a alojar tus archivos o migrar un servidor; diseñamos un entorno seguro, veloz y escalable en la nube, y desarrollamos las soluciones digitales necesarias para garantizar que tus sistemas críticos operen sin interrupciones, protegidos contra fallas y alineados al crecimiento de tu organización.
            </p>

            <ul className="solution-list">
              <li className="solution-item">
                <div className="solution-check">
                  <CheckCircle2 size={16} />
                </div>
                <div>
                  <h4 className="solution-item-title">Transición a la Nube Sin Pausas</h4>
                  <p className="solution-item-desc">Llevamos tus sistemas actuales de ventas, administración o contabilidad a la nube de forma planificada y segura, garantizando que tu empresa siga operando sin interrupción alguna.</p>
                </div>
              </li>

              <li className="solution-item">
                <div className="solution-check">
                  <CheckCircle2 size={16} />
                </div>
                <div>
                  <h4 className="solution-item-title">Respaldos Automáticos y Máxima Seguridad</h4>
                  <p className="solution-item-desc">Tus sistemas protegidos con accesos restringidos y copias de seguridad diarias automatizadas fuera de tus oficinas físicas, para que tu información comercial esté siempre a salvo ante cualquier accidente.</p>
                </div>
              </li>

              <li className="solution-item">
                <div className="solution-check">
                  <CheckCircle2 size={16} />
                </div>
                <div>
                  <h4 className="solution-item-title">Modernización y Continuidad del Servicio</h4>
                  <p className="solution-item-desc">Evolucionamos la forma en que corren tus aplicaciones, eliminando tareas manuales repetitivas para que tus servicios sean sumamente rápidos, estables y eficientes.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 4. SERVICES SECTION */}
      <section id="servicios" className="section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center">
            <span className="section-subtitle">Nuestra Especialidad</span>
            <h2 className="section-title">Servicios diseñados para tu evolución digital</h2>
            <p className="section-desc">
              Ofrecemos soluciones modulares y especializadas adaptadas al tamaño y necesidades reales de tu empresa en Paraguay.
            </p>
          </div>

          <div className="services-grid">
            {/* Card 1: Cloud Assessment */}
            <div className="glass-card service-card">
              <div className="service-icon-wrapper">
                <Compass size={28} />
              </div>
              <h3 className="service-title">Diagnóstico de Sistemas</h3>
              <p className="service-desc">
                Analizamos detalladamente el estado actual de tus sistemas de ventas, facturación o contabilidad. Identificamos riesgos de pérdida de datos y te mostramos el camino más seguro y económico para llevarlos a la nube.
              </p>
              <div className="service-tags">
                <span className="service-tag">Prevención de Fallas</span>
                <span className="service-tag">Plan de Ahorro</span>
                <span className="service-tag">Plan a la Nube</span>
              </div>
            </div>

            {/* Card 2: Migración Cloud */}
            <div className="glass-card service-card">
              <div className="service-icon-wrapper">
                <Cloud size={28} />
              </div>
              <h3 className="service-title">Migración e Integración</h3>
              <p className="service-desc">
                Trasladamos tus sistemas comerciales, facturación electrónica y bases de datos a un entorno en la nube seguro y veloz, garantizando que tu información histórica permanezca 100% intacta.
              </p>
              <div className="service-tags">
                <span className="service-tag">Facturación y ERP</span>
                <span className="service-tag">Seguridad de Datos</span>
                <span className="service-tag">Alta Velocidad</span>
              </div>
            </div>

            {/* Card 3: Desarrollo & Modernización */}
            <div className="glass-card service-card">
              <div className="service-icon-wrapper">
                <RefreshCw size={28} />
              </div>
              <h3 className="service-title">Desarrollo & Modernización</h3>
              <p className="service-desc">
                Diseñamos y desarrollamos soluciones y sistemas de software a medida adaptados a las necesidades reales de tu negocio, impulsando la productividad de tus equipos y la automatización de procesos.
              </p>
              <div className="service-tags">
                <span className="service-tag">Software a Medida</span>
                <span className="service-tag">Mayor Productividad</span>
                <span className="service-tag">Evolución Continua</span>
              </div>
            </div>

            {/* Card 4: DevOps & Automatización */}
            <div className="glass-card service-card">
              <div className="service-icon-wrapper">
                <Terminal size={28} />
              </div>
              <h3 className="service-title">Automatización y Continuidad</h3>
              <p className="service-desc">
                Eliminamos las tareas manuales y configuraciones propensas a errores. Hacemos que tus actualizaciones de sistemas se realicen de forma automática, ágil y sin interrupciones para tus usuarios.
              </p>
              <div className="service-tags">
                <span className="service-tag">Cero Errores</span>
                <span className="service-tag">Procesos Ágiles</span>
                <span className="service-tag">Seguridad Digital</span>
              </div>
            </div>

            {/* Card 5: Servicios Administrados */}
            <div className="glass-card service-card" style={{ gridColumn: 'span 1' }}>
              <div className="service-icon-wrapper">
                <Settings size={28} />
              </div>
              <h3 className="service-title">Soporte y Monitoreo Continuo</h3>
              <p className="service-desc">
                Nos convertimos en tu equipo de soporte tecnológico. Vigilamos el correcto funcionamiento de tus sistemas en la nube 24/7, realizamos respaldos diarios y resolvemos dudas de inmediato.
              </p>
              <div className="service-tags">
                <span className="service-tag">Vigilancia 24/7</span>
                <span className="service-tag">Soporte Dedicado</span>
                <span className="service-tag">Respaldos Diarios</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. PLANS SECTION */}
      <section id="planes" className="section">
        <div className="container">
          <div className="text-center">
            <span className="section-subtitle">Paquetes Flexibles</span>
            <h2 className="section-title">Esquemas de trabajo orientados a tu crecimiento</h2>
            <p className="section-desc">
              Selecciona el modelo de acompañamiento que mejor se adapte al momento tecnológico de tu organización.
            </p>
          </div>

          <div className="plans-grid">
            {/* Plan 1 */}
            <div className="glass-card plan-card">
              <h3 className="plan-name">Diagnóstico y Plan Cloud</h3>
              <p className="plan-subtitle">Ideal para realizar un diagnóstico completo antes de dar cualquier paso en la nube.</p>
              
              <div className="plan-price-box">
                <div className="plan-price-label">Inversión</div>
                <div className="plan-price-value">A Medida</div>
              </div>

              <ul className="plan-features-list">
                <li className="plan-feature">
                  <CheckCircle2 size={16} className="plan-feature-icon" />
                  <span>Análisis completo de sistemas actuales</span>
                </li>
                <li className="plan-feature">
                  <CheckCircle2 size={16} className="plan-feature-icon" />
                  <span>Identificación de riesgos y fugas</span>
                </li>
                <li className="plan-feature">
                  <CheckCircle2 size={16} className="plan-feature-icon" />
                  <span>Estimación de costos mensuales en la nube</span>
                </li>
                <li className="plan-feature">
                  <CheckCircle2 size={16} className="plan-feature-icon" />
                  <span>Plan ordenado de transición</span>
                </li>
              </ul>

              <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => scrollToId('contacto')}>
                Consultar presupuesto
              </button>
            </div>

            {/* Plan 2: Popular */}
            <div className="glass-card plan-card popular">
              <div className="plan-popular-tag">Más Solicitado</div>
              <h3 className="plan-name">Plan Migración Cloud</h3>
              <p className="plan-subtitle">Perfecto para empresas listas para llevar sus sistemas de ventas, facturación o contabilidad a la nube.</p>
              
              <div className="plan-price-box">
                <div className="plan-price-label">Inversión</div>
                <div className="plan-price-value">Migración</div>
              </div>

              <ul className="plan-features-list">
                <li className="plan-feature">
                  <CheckCircle2 size={16} className="plan-feature-icon" />
                  <span>Migración completa de sistemas y datos</span>
                </li>
                <li className="plan-feature">
                  <CheckCircle2 size={16} className="plan-feature-icon" />
                  <span>Configuración de accesos seguros y restringidos</span>
                </li>
                <li className="plan-feature">
                  <CheckCircle2 size={16} className="plan-feature-icon" />
                  <span>Respaldos automáticos y redundancia</span>
                </li>
                <li className="plan-feature">
                  <CheckCircle2 size={16} className="plan-feature-icon" />
                  <span>Soporte y monitoreo post-migración incluido</span>
                </li>
              </ul>

              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => scrollToId('contacto')}>
                Consultar presupuesto
              </button>
            </div>

            {/* Plan 3 */}
            <div className="glass-card plan-card">
              <h3 className="plan-name">Plan Evolution Cloud</h3>
              <p className="plan-subtitle">Acompañamiento tecnológico continuo, automatización total y máxima agilidad para tu negocio.</p>
              
              <div className="plan-price-box">
                <div className="plan-price-label">Inversión</div>
                <div className="plan-price-value">Suscripción</div>
              </div>

              <ul className="plan-features-list">
                <li className="plan-feature">
                  <CheckCircle2 size={16} className="plan-feature-icon" />
                  <span>Gestión moderna y estandarizada de sistemas</span>
                </li>
                <li className="plan-feature">
                  <CheckCircle2 size={16} className="plan-feature-icon" />
                  <span>Actualizaciones automáticas sin interrupciones</span>
                </li>
                <li className="plan-feature">
                  <CheckCircle2 size={16} className="plan-feature-icon" />
                  <span>Vigilancia permanente y alertas inmediatas</span>
                </li>
                <li className="plan-feature">
                  <CheckCircle2 size={16} className="plan-feature-icon" />
                  <span>Acompañamiento mensual para mejoras de rendimiento</span>
                </li>
              </ul>

              <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => scrollToId('contacto')}>
                Consultar presupuesto
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 6. PROCESS SECTION */}
      <section id="proceso" className="section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center">
            <span className="section-subtitle">¿Cómo trabajamos?</span>
            <h2 className="section-title">El camino ordenado hacia la nube</h2>
            <p className="section-desc">
              Garantizamos orden, claridad y seguridad en cada una de las fases de implementación y soporte de tus servicios cloud.
            </p>
          </div>

          <div className="process-timeline">
            {/* Step 1 */}
            <div className="process-step">
              <div className="process-badge">1</div>
              <h4 className="process-step-title">Diagnóstico Gratuito</h4>
              <p className="process-step-desc">Evaluación inicial sin costo de tus sistemas comerciales actuales.</p>
            </div>

            {/* Step 2 */}
            <div className="process-step">
              <div className="process-badge">2</div>
              <h4 className="process-step-title">Plan de Trabajo</h4>
              <p className="process-step-desc">Diseño a medida de tus sistemas en la nube y un presupuesto totalmente transparente.</p>
            </div>

            {/* Step 3 */}
            <div className="process-step">
              <div className="process-badge">3</div>
              <h4 className="process-step-title">Migración</h4>
              <p className="process-step-desc">Ejecución del traspaso resguardando la integridad y disponibilidad de tus datos.</p>
            </div>

            {/* Step 4 */}
            <div className="process-step">
              <div className="process-badge">4</div>
              <h4 className="process-step-title">Seguridad y Control</h4>
              <p className="process-step-desc">Configuración de accesos blindados y mecanismos de respaldo automático.</p>
            </div>

            {/* Step 5 */}
            <div className="process-step">
              <div className="process-badge">5</div>
              <h4 className="process-step-title">Soporte Dedicado</h4>
              <p className="process-step-desc">Vigilancia permanente de tus sistemas, soporte dedicado y mejoras continuas.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. DIFFERENTIATORS SECTION */}
      <section className="section">
        <div className="container">
          <div className="text-center">
            <span className="section-subtitle">Por Qué Nosotros</span>
            <h2 className="section-title">Diseñado especialmente para la realidad de tu PYME</h2>
            <p className="section-desc">
              Evitamos tecnicismos vacíos de marketing. Nos enfocamos en ofrecerte seguridad, automatización y evolución tecnológica real.
            </p>
          </div>

          <div className="diff-grid">
            <div className="glass-card diff-card">
              <div className="diff-icon-wrapper">
                <Users size={20} />
              </div>
              <h4 className="diff-title">Enfoque en PYMEs</h4>
              <p className="diff-desc">Entendemos tus límites y prioridades comerciales. Soluciones realistas, eficientes y seguras.</p>
            </div>

            <div className="glass-card diff-card">
              <div className="diff-icon-wrapper">
                <Shield size={20} />
              </div>
              <h4 className="diff-title">Sistemas Automatizados</h4>
              <p className="diff-desc">Eliminamos la configuración manual de tus sistemas. Todo corre de forma estandarizada, reduciendo fallas y demoras.</p>
            </div>

            <div className="glass-card diff-card">
              <div className="diff-icon-wrapper">
                <Zap size={20} />
              </div>
              <h4 className="diff-title">Estándares Internacionales</h4>
              <p className="diff-desc">Implementamos tus sistemas siguiendo las mejores prácticas globales de seguridad, velocidad y alta disponibilidad en la nube.</p>
            </div>

            <div className="glass-card diff-card">
              <div className="diff-icon-wrapper">
                <RefreshCw size={20} />
              </div>
              <h4 className="diff-title">Suscripción y Cercanía</h4>
              <p className="diff-desc">Soporte y acompañamiento mensual constante. Evolucionamos a la par de tu negocio.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. ARCHITECTURE FLOW DIAGRAM SECTION */}
      <section className="section" style={{ background: 'linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)' }}>
        <div className="container architecture-container">
          <div className="arch-visual">
            {/* Custom SVG Architecture Diagram Flow */}
            <svg viewBox="0 0 600 350" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: 'auto' }}>
              <defs>
                <linearGradient id="glow-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#00D8FF" />
                  <stop offset="100%" stopColor="#00ADB5" />
                </linearGradient>
              </defs>

              {/* Connecting Flow Lines */}
              <path d="M90 175 L170 175" stroke="#00ADB5" strokeWidth="2.5" className="arch-line" />
              <path d="M230 175 L310 175" stroke="#00ADB5" strokeWidth="2.5" className="arch-line" />
              
              {/* Branch to AWS CloudWatch & Alertas */}
              <path d="M370 175 L450 120" stroke="#00D8FF" strokeWidth="2.5" className="arch-line" />
              <path d="M370 175 L450 230" stroke="#00D8FF" strokeWidth="2.5" className="arch-line" />
              
              <path d="M495 120 L530 175" stroke="#00ADB5" strokeWidth="1.5" strokeDasharray="3 3" />
              <path d="M495 230 L530 175" stroke="#00ADB5" strokeWidth="1.5" strokeDasharray="3 3" />

              {/* Node 1: GitHub */}
              <g className="arch-node">
                <rect x="30" y="145" width="60" height="60" rx="10" fill="#0B192C" stroke="#00D8FF" strokeWidth="2" />
                <path d="M60 155 C52 155 45 162 45 170 C45 177 50 182 56 184 C57 184 57 184 57 183 C57 182 57 180 57 178 C52 179 51 176 51 176 C50 174 49 173 49 173 C47 172 49 172 49 172 C51 172 52 174 52 174 C53 176 55 175 56 175 C56 173 57 172 57 171 C53 171 49 169 49 162 C49 160 50 158 51 157 C51 157 50 154 51 151 C51 151 53 151 56 153 C57 152 59 152 60 152 C61 152 63 152 64 153 C67 151 69 151 69 151 C70 154 69 157 69 157 C70 158 71 160 71 162 C71 169 67 171 63 171 C64 172 65 174 65 177 C65 180 65 182 65 183 C65 184 65 184 66 184 C72 182 77 177 77 170 C77 162 70 155 60 155 Z" fill="#00D8FF" />
                <text x="60" y="222" fill="#00D8FF" fontSize="10" fontFamily="var(--font-heading)" fontWeight="600" textAnchor="middle">GitHub</text>
              </g>

              {/* Node 2: GitHub Actions */}
              <g className="arch-node">
                <rect x="170" y="145" width="60" height="60" rx="10" fill="#0B192C" stroke="#00ADB5" strokeWidth="2" />
                {/* Gears representation */}
                <circle cx="200" cy="175" r="14" stroke="#00ADB5" strokeWidth="2" strokeDasharray="4 2" fill="none" />
                <circle cx="200" cy="175" r="6" fill="#00ADB5" />
                <text x="200" y="222" fill="#00ADB5" fontSize="10" fontFamily="var(--font-heading)" fontWeight="600" textAnchor="middle">Actions</text>
              </g>

              {/* Node 3: Terraform */}
              <g className="arch-node">
                <rect x="310" y="145" width="60" height="60" rx="10" fill="#0B192C" stroke="#00D8FF" strokeWidth="2" />
                {/* Terraform dimensional shape */}
                <path d="M330 162 L340 157 L340 173 L330 178 Z M342 175 L352 170 L352 186 L342 191 Z M342 157 L352 152 L352 168 L342 173 Z M330 178 L340 173 L340 189 L330 194 Z" fill="#00D8FF" />
                <text x="340" y="222" fill="#00D8FF" fontSize="10" fontFamily="var(--font-heading)" fontWeight="600" textAnchor="middle">Terraform</text>
              </g>

              {/* Node 4: AWS */}
              <g className="arch-node">
                <rect x="450" y="90" width="60" height="60" rx="10" fill="#0B192C" stroke="#00ADB5" strokeWidth="2" />
                <path d="M472 110 L480 105 L488 110 L488 120 C488 128 480 133 480 133 C480 133 472 128 472 120 Z" stroke="#00ADB5" strokeWidth="2" fill="none" />
                <text x="480" y="167" fill="#00ADB5" fontSize="10" fontFamily="var(--font-heading)" fontWeight="600" textAnchor="middle">Nube Segura</text>
              </g>

              {/* Node 5: CloudWatch & Alertas */}
              <g className="arch-node">
                <rect x="450" y="200" width="60" height="60" rx="10" fill="#0B192C" stroke="#00D8FF" strokeWidth="2" />
                <path d="M468 238 L475 220 L482 233 L487 225 L492 238" stroke="#00D8FF" strokeWidth="2.5" fill="none" />
                <text x="480" y="277" fill="#00D8FF" fontSize="10" fontFamily="var(--font-heading)" fontWeight="600" textAnchor="middle">Alertas</text>
              </g>

              {/* Final Node: Cliente */}
              <g className="arch-node">
                <circle cx="550" cy="175" r="20" fill="url(#glow-grad)" />
                <path d="M544 178 C544 173 547 170 550 170 C553 170 556 173 556 178 M550 166 C552.2 166 554 164.2 554 162 C554 159.8 552.2 158 550 158 C547.8 158 546 159.8 546 162 C546 164.2 547.8 166 550 166 Z" stroke="#0B192C" strokeWidth="2.5" fill="none" />
                <text x="550" y="212" fill="#text-primary" fontSize="10" fontFamily="var(--font-heading)" fontWeight="700" textAnchor="middle">Cliente</text>
              </g>
            </svg>
          </div>

          <div className="arch-info">
            <span className="section-subtitle" style={{ display: 'block', textAlign: 'left' }}>Enfoque Tecnológico</span>
            <h3 className="arch-info-title">Sistemas Seguros, Estables y Controlados</h3>
            <p className="arch-info-desc">
              Utilizamos tecnologías líderes para que tu pequeña o mediana empresa funcione con la máxima estabilidad, protección y control.
            </p>

            <div className="arch-list">
              <div className="arch-list-item">
                <CheckCircle2 size={18} className="arch-list-dot" />
                <p className="arch-list-text">
                  <strong>Historial y Control:</strong> Cada cambio en la configuración de tus sistemas queda registrado de manera transparente y segura, permitiendo volver atrás ante cualquier imprevisto.
                </p>
              </div>

              <div className="arch-list-item">
                <CheckCircle2 size={18} className="arch-list-dot" />
                <p className="arch-list-text">
                  <strong>Actualizaciones Continuas:</strong> Modificamos y mejoramos tus aplicaciones sin interrumpir tus ventas ni tus operaciones diarias.
                </p>
              </div>

              <div className="arch-list-item">
                <CheckCircle2 size={18} className="arch-list-dot" />
                <p className="arch-list-text">
                  <strong>Monitoreo Inteligente:</strong> Tus sistemas en la nube se vigilan de manera automatizada. Si hay alguna inestabilidad, el sistema nos alerta antes de que tu negocio se vea afectado.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 9. CTA FINAL */}
      <section className="section" style={{ paddingBottom: '4rem' }}>
        <div className="container">
          <div className="glass-card cta-final-card">
            <h2 className="cta-final-title text-gradient">¿Tu empresa está lista para dar el siguiente paso hacia la nube?</h2>
            <p className="cta-final-desc">
              Hablemos sobre tus sistemas actuales y preparemos un plan de evolución digital adaptado a la realidad y el tamaño de tu negocio en Paraguay.
            </p>
            <button className="btn btn-primary" onClick={() => scrollToId('contacto')}>
              Comenzar Diagnóstico
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* 10. CONTACT FORM SECTION */}
      <section id="contacto" className="section" style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-glass)' }}>
        <div className="container contact-wrapper">
          <div className="contact-info">
            <span className="section-subtitle" style={{ display: 'block', textAlign: 'left' }}>Contacto Directo</span>
            <h2 className="section-title" style={{ textAlign: 'left', marginBottom: '1.5rem' }}>Escribinos para conversar</h2>
            <p className="hero-subtitle" style={{ fontSize: '1rem', marginBottom: '2rem' }}>
              Completá el formulario para solicitar un diagnóstico gratuito de tus sistemas y ver cómo podemos ayudarte a dar el salto a la nube.
            </p>

            <div className="contact-info-list">
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <Mail size={20} />
                </div>
                <div>
                  <span className="contact-info-label">Email Corporativo</span>
                  <div className="contact-info-value">
                    <a href="mailto:contacto@cloudgate.com.py">contacto@cloudgate.com.py</a>
                  </div>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <Phone size={20} />
                </div>
                <div>
                  <span className="contact-info-label">WhatsApp Soporte</span>
                  <div className="contact-info-value">
                    <a href="https://wa.me/595992399996" target="_blank" rel="noopener noreferrer">0992 399 996</a>
                  </div>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <MapPin size={20} />
                </div>
                <div>
                  <span className="contact-info-label">Ubicación</span>
                  <div className="contact-info-value">Asunción, Paraguay</div>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-card">
            <form onSubmit={handleSubmit} className="contact-form">
              <h3 style={{ fontSize: '1.3rem', marginBottom: '0.5rem', fontFamily: 'var(--font-heading)' }}>Solicitud de Presupuesto</h3>
              
              {formStatus === 'success' && (
                <div className="form-alert form-alert-success">
                  <CheckCircle2 size={20} style={{ flexShrink: 0 }} />
                  <span>{formMessage}</span>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="form-alert form-alert-error">
                  <AlertCircle size={20} style={{ flexShrink: 0 }} />
                  <span>{formMessage}</span>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Nombre Completo *</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name} 
                    onChange={handleInputChange} 
                    placeholder="Ej. Juan Pérez" 
                    className="form-input" 
                    required 
                    disabled={formStatus === 'loading'}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="company" className="form-label">Empresa / Comercio</label>
                  <input 
                    type="text" 
                    id="company" 
                    name="company" 
                    value={formData.company} 
                    onChange={handleInputChange} 
                    placeholder="Ej. Estudio Contable Pérez" 
                    className="form-input"
                    disabled={formStatus === 'loading'}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email de Contacto *</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleInputChange} 
                    placeholder="Ej. juan@empresa.com.py" 
                    className="form-input" 
                    required 
                    disabled={formStatus === 'loading'}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Teléfono / Celular</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    value={formData.phone} 
                    onChange={handleInputChange} 
                    placeholder="Ej. +595 981 123456" 
                    className="form-input"
                    disabled={formStatus === 'loading'}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="service" className="form-label">Servicio de Interés</label>
                <select 
                  id="service" 
                  name="service" 
                  value={formData.service} 
                  onChange={handleInputChange} 
                  className="form-input form-select"
                  disabled={formStatus === 'loading'}
                >
                  <option value="Cloud Assessment">Diagnóstico de Sistemas (Auditoría Inicial)</option>
                  <option value="Migración Cloud">Migración e Integración Cloud</option>
                  <option value="Modernización Tecnológica">Desarrollo y Modernización de Sistemas a Medida</option>
                  <option value="DevOps & Automatización">Automatización y Continuidad de Sistemas</option>
                  <option value="Servicios Administrados">Monitoreo y Soporte Dedicado</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Mensaje o Detalle del Requerimiento *</label>
                <textarea 
                  id="message" 
                  name="message" 
                  value={formData.message} 
                  onChange={handleInputChange} 
                  placeholder="Contanos un poco sobre tu situación actual: ¿qué sistema usás hoy? ¿dónde está instalado?" 
                  rows={4} 
                  className="form-input" 
                  style={{ resize: 'vertical' }}
                  required
                  disabled={formStatus === 'loading'}
                ></textarea>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary" 
                style={{ width: '100%', marginTop: '0.5rem' }}
                disabled={formStatus === 'loading'}
              >
                {formStatus === 'loading' ? 'Enviando solicitud...' : 'Enviar Solicitud de Presupuesto'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER SECTION */}
      <footer className="footer">
        <div className="container footer-grid">
          <div>
            <div className="logo" style={{ marginBottom: '1rem' }}>
              <Cloud className="logo-icon" size={24} />
              <span>Cloud<span className="text-teal-gradient">GatePY</span></span>
            </div>
            <p className="footer-logo-desc">
              Evolución tecnológica, seguridad e infraestructura cloud para microempresas y PYMEs en Paraguay. Desplegamos soluciones estables y a la escala real de tu negocio.
            </p>
          </div>

          <div>
            <h4 className="footer-title">Enlaces</h4>
            <ul className="footer-links">
              <li>
                <a href="#inicio" className="footer-link" onClick={(e) => { e.preventDefault(); scrollToId('inicio'); }}>
                  Inicio
                </a>
              </li>
              <li>
                <a href="#problema" className="footer-link" onClick={(e) => { e.preventDefault(); scrollToId('problema'); }}>
                  Problemas Comunes
                </a>
              </li>
              <li>
                <a href="#servicios" className="footer-link" onClick={(e) => { e.preventDefault(); scrollToId('servicios'); }}>
                  Servicios Cloud
                </a>
              </li>
              <li>
                <a href="#planes" className="footer-link" onClick={(e) => { e.preventDefault(); scrollToId('planes'); }}>
                  Planes de Inversión
                </a>
              </li>
              <li>
                <a href="#contacto" className="footer-link" onClick={(e) => { e.preventDefault(); scrollToId('contacto'); }}>
                  Contacto
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="footer-title">Contacto & Redes</h4>
            <p style={{ fontSize: '0.9rem', marginBottom: '0.8rem' }}>Asunción, Paraguay</p>
            <p style={{ fontSize: '0.9rem', marginBottom: '1.2rem' }}>
              <a href="mailto:contacto@cloudgate.com.py" style={{ color: 'var(--color-cyan)' }}>contacto@cloudgate.com.py</a>
            </p>
            <div className="footer-socials">
              <a href="https://linkedin.com/company/cloudgatepy" target="_blank" rel="noopener noreferrer" className="footer-social-icon" aria-label="LinkedIn">
                <Users size={18} />
              </a>
              <a href="https://github.com/cloudgatepy" target="_blank" rel="noopener noreferrer" className="footer-social-icon" aria-label="GitHub">
                <Terminal size={18} />
              </a>
              <a href="https://wa.me/595992399996" target="_blank" rel="noopener noreferrer" className="footer-social-icon" aria-label="WhatsApp">
                <Phone size={18} />
              </a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} CloudGatePY. Todos los derechos reservados. Asunción, Paraguay.</p>
        </div>
      </footer>

      {/* Floating WhatsApp Widget */}
      <a 
        href="https://wa.me/595992399996" 
        target="_blank" 
        rel="noopener noreferrer" 
        className="whatsapp-floating"
        aria-label="Escríbenos por WhatsApp"
      >
        <svg viewBox="0 0 16 16" width="30" height="30" fill="currentColor">
          <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
        </svg>
        <span className="whatsapp-tooltip">¿Conversamos por WhatsApp?</span>
      </a>
    </div>
  );
}
